"use client";

import { cn } from "./utils";
import { motion } from "motion/react";
import { ReactNode } from "react";

export interface MetricTab {
  id: string;
  label: string;
  value: string | number;
  subValue?: string;
  icon?: ReactNode;
  colorClassName?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

export interface FundraisingMetricsTabsProps {
  tabs: MetricTab[];
  selectedTabId?: string;
  onSelectTab?: (tabId: string) => void;
  formatValue?: (value: string | number) => string;
  className?: string;
}

/**
 * A metrics tabs component for displaying key fundraising metrics.
 * Similar to the analytics tabs in the main analytics component.
 */
export function FundraisingMetricsTabs({
  tabs,
  selectedTabId,
  onSelectTab,
  className,
}: FundraisingMetricsTabsProps) {
  return (
    <div
      className={cn(
        "grid divide-x divide-neutral-200 overflow-hidden rounded-t-xl border border-neutral-200",
        className
      )}
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((tab, index) => {
        const isSelected = selectedTabId === tab.id;
        const isClickable = !!onSelectTab;

        return (
          <motion.div
            key={tab.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: index * 0.05 }}
            className={cn(
              "relative p-4 sm:p-6 bg-white",
              isClickable && "cursor-pointer hover:bg-neutral-50 transition-colors",
              index === 0 && "rounded-tl-xl"
            )}
            onClick={() => onSelectTab?.(tab.id)}
          >
            {/* Active indicator */}
            {isSelected && (
              <motion.div
                layoutId="activeTab"
                className="absolute bottom-0 left-0 h-0.5 w-full bg-black"
              />
            )}

            {/* Label */}
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-1">
              {tab.icon && (
                <div className={cn("w-2 h-2 rounded-sm", tab.colorClassName || "bg-blue-500")} />
              )}
              <span>{tab.label}</span>
            </div>

            {/* Value */}
            <div className="flex items-baseline gap-2">
              <span className="text-2xl sm:text-3xl font-semibold text-neutral-900">
                {tab.value}
              </span>
              {tab.trend && (
                <span
                  className={cn(
                    "text-xs font-medium",
                    tab.trend.isPositive ? "text-emerald-600" : "text-red-600"
                  )}
                >
                  {tab.trend.isPositive ? "+" : ""}
                  {tab.trend.value}%
                </span>
              )}
            </div>

            {/* Sub value */}
            {tab.subValue && (
              <div className="text-xs text-neutral-400 mt-1">{tab.subValue}</div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

export interface FundraisingMetricsGridProps {
  metrics: {
    label: string;
    value: string | number;
    hint?: string;
    icon?: ReactNode;
    colorClassName?: string;
  }[];
  columns?: 2 | 3 | 4 | 5 | 6;
  className?: string;
}

/**
 * A grid layout for displaying fundraising metrics.
 * More flexible than tabs, good for dashboards.
 */
export function FundraisingMetricsGrid({
  metrics,
  columns = 4,
  className,
}: FundraisingMetricsGridProps) {
  const gridCols = {
    2: "grid-cols-2",
    3: "grid-cols-2 md:grid-cols-3",
    4: "grid-cols-2 md:grid-cols-4",
    5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 md:grid-cols-3 lg:grid-cols-6",
  };

  return (
    <div className={cn("grid gap-4", gridCols[columns], className)}>
      {metrics.map((metric, index) => (
        <motion.div
          key={metric.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="rounded-xl border border-neutral-200 p-4 sm:p-5 bg-white shadow-sm"
        >
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 font-medium">
            {metric.icon}
            {metric.label}
          </div>
          <div className={cn("mt-1 text-2xl font-semibold", metric.colorClassName || "text-neutral-900")}>
            {metric.value}
          </div>
          {metric.hint && (
            <div className="mt-1 text-[12px] text-neutral-500">{metric.hint}</div>
          )}
        </motion.div>
      ))}
    </div>
  );
}
